import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { Inquiry, InquiryPaymentStatus } from '@tv3/store/inquiry/inquiry.model'
import { FeeTypes, InquiryCost, isExternalBookingSource } from '@tokeet/cost-resolver'
import {
  Destroyable,
  distinctUntilChangedByIds,
  isSomething,
  Rental,
  selectRentalById,
  TokeetCurrencyPipe,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import { filter, map, switchMap, tap } from 'rxjs/operators'
import { InquiryChargeService } from '@tv3/services/inquiry-charge.service'
import { InquiryService } from '@tv3/store/inquiry/inquiry.service'
import { select, Store } from '@ngrx/store'
import { InquirySharedDialogsService } from '@tv3/containers/inquiries/dialogs/inquiry-shared-dialogs.service'
import { BookingFee, BookingFeesService, FeeModalityLabels } from '@tokeet-frontend/rentals'
import { InquiryChannelBreakdownDialogsService } from '@tv3/containers/inquiries/dialogs/inquiry-channel-breakdown-dialog'

@Component({
  selector: 'app-overlay-booking-charges',
  templateUrl: './booking-charges.component.html',
  styleUrls: ['./booking-charges.component.scss'],
})
export class BookingChargesComponent extends Destroyable implements OnInit {
  private inquiry$ = new BehaviorSubject<Inquiry>(null)
  @Input() set inquiry(inquiry: Inquiry) {
    this.inquiry$.next(inquiry)
  }
  get inquiry() {
    return this.inquiry$.value
  }

  @Input() direction = 'h'

  @Input() applicableFees = true
  applicableFees$: Observable<BookingFee[]>
  feeTypes = FeeTypes
  feeModalityLabels = FeeModalityLabels

  @Input() readonly: boolean

  rental: Rental

  charges: InquiryCost
  formulaAdjustedCost: number

  get currency() {
    return this.rental?.currency
  }
  get currencyCode() {
    return this.rental?.currency?.code
  }

  status: { paid: boolean; remaining: number; status: InquiryPaymentStatus }

  getFeeName(fee: string) {
    switch (fee) {
      case `Administrative Fee`:
        return $localize`Administrative Fee`
      case `Booking Fee`:
        return $localize`Booking Fee`
      case `Cleaning Fee`:
        return $localize`Cleaning Fee`
      case `Dock Fee`:
        return $localize`Dock Fee`
      case `Damage Deposit`:
        return $localize`Damage Deposit`
      case `Guest Registration`:
        return $localize`Guest Registration`
      case `HOA Fee`:
        return $localize`HOA Fee`
      case `Hot Tub Fee`:
        return $localize`Hot Tub Fee`
      case `Management Fee`:
        return $localize`Management Fee`
      case `Parking Fee`:
        return $localize`Parking Fee`
      case `Pool Fee`:
        return $localize`Pool Fee`
      case `Pool Heating Fee`:
        return $localize`Pool Heating Fee`
      case `Resort Fee`:
        return $localize`Resort Fee`
      case `Security Deposit Fee`:
        return $localize`Security Deposit Fee`
      case `Service Fee`:
        return $localize`Service Fee`
      case `Utility Fee`:
        return $localize`Utility Fee`
      case `Waiver Damage Fee`:
        return $localize`Waiver Damage Fee`
      case `Miscellaneous Fee`:
        return $localize`Miscellaneous Fee`
      case `Per Extra Person Fee`:
        return $localize`Per Extra Person Fee`
      default:
        return fee
    }
  }

  getStatusTooltip(status: InquiryPaymentStatus) {
    switch (status) {
      case InquiryPaymentStatus.Paid:
        return $localize`Paid`
      case InquiryPaymentStatus.Unpaid:
        return $localize`Unpaid`
      case InquiryPaymentStatus.PartialPaid:
        return $localize`Partial Paid`
    }
  }

  getBookingFormulaTooltip() {
    return (
      $localize`Booking costs were adjusted via the booking formula set on the '` +
      this.inquiry$.value?.inquirySource +
      $localize`' Channel Details settings`
    )
  }

  getRemainingTooltip() {
    return isSomething(this.status?.remaining)
      ? $localize`Remaining: ` + this.tokeetCurrencyPipe.transform(this.status?.remaining, this.currency)
      : ''
  }

  get isExternalBooking() {
    return (
      !!this.inquiry?.booked && isSomething(this.inquiry?.cost) && isExternalBookingSource(this.inquiry?.inquirySource)
    )
  }
  hasChannelPrice = false

  constructor(
    private store: Store<any>,
    private inquiryChargeService: InquiryChargeService,
    private inquiryService: InquiryService,
    private bookingFeesService: BookingFeesService,
    private tokeetCurrencyPipe: TokeetCurrencyPipe,
    private sharedDialogsService: InquirySharedDialogsService,
    private inquiryChannelBreakdownDialogs: InquiryChannelBreakdownDialogsService
  ) {
    super()
  }

  ngOnInit() {
    this.inquiry$
      .pipe(
        filter(isSomething),
        tap((inquiry) => {
          this.hasChannelPrice = this.inquiryChannelBreakdownDialogs.hasChannelPrice(inquiry)
        }),
        switchMap((inquiry) =>
          combineLatest([
            this.inquiryChargeService.getCharges(inquiry),
            this.inquiryService.getCostAfterFormula(inquiry.id),
            this.store.pipe(select(selectRentalById(inquiry.rentalId))),
            this.inquiryChargeService.getBookingPaymentStatusForInquiry(inquiry),
          ])
        ),
        untilDestroy(this)
      )
      .subscribe(([charges, cost, rental, status]) => {
        this.charges = charges
        this.formulaAdjustedCost = cost
        this.rental = rental
        this.status = status
      })

    this.applicableFees$ = this.inquiry$.pipe(
      filter(isSomething),
      distinctUntilChangedByIds(),
      switchMap((inquiry) => this.bookingFeesService.getApplicableFeesForInquiry(inquiry, inquiry.account, false))
    )
  }

  onUpdateQuote() {
    this.sharedDialogsService.openUpdateQuote(this.inquiry)
  }

  onChannelBreakdown() {
    this.inquiryChannelBreakdownDialogs.onChannelBreakdown(this.inquiry, this.currency?.symbol || this.currency.code)
  }
}
